import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { updateUserMainImage, createUser } from '@/services/userService';

import {
  TextField,
  Grid,
  Box,
  Button,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import { chatWithGPT } from '@/services/openAiService';
import { useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { getUserValidationSchema as validationSchema } from '../UserForm/Validations/ValidationUserSchema';
import { createUserTypeRelationship } from '../../../../services/userTypeRelationshipService';
import { getOrdinal } from '../SpacesForm/Validations/NumberOrdinalConfig';
import { useSelector } from 'react-redux';
import Label from '@/components/form/Label/Label';

const UseForm = () => {
  //const { mutate } = useCreateUser();
  const [successMessage, setSuccessMessage] = useState('');
  const [chatLog, setChatLog] = useState([]);
  const language = useSelector(state => state.language);

  const editorRef = useRef([]);

  const handleSubmit = async (values, { resetForm }) => {
    console.log(JSON.stringify(values));
    try {
      const createdUser = await createUser(values);
      if (!createdUser || !createdUser.user_id) {
        throw new Error('No se recibió un user_id válido al crear el usuario');
      }

      const userId = createdUser.user_id;
      console.log('User ID obtenido de la respuesta:', userId);

      if (values.main_image && values.main_image.length > 0) {
        await updateUserMainImage(userId, values.main_image);
      }

      let subtypeRelationships = values?.subtypeRelationship || [];

      if (!Array.isArray(subtypeRelationships)) {
        subtypeRelationships = [subtypeRelationships];
      }

      subtypeRelationships = subtypeRelationships.slice(0, 3).map(subtype => ({
        ...subtype,
        user_id: userId,
      }));

      if (subtypeRelationships.length > 0) {
        for (const relationshipData of subtypeRelationships) {
          await createUserTypeRelationship(relationshipData);
        }
        console.log('Relaciones de subtipo creadas con éxito');
      } else {
        console.warn('No hay relaciones de subtipo para enviar.');
      }
      const successMessageText =
        language === 'ES'
          ? 'Usuario creado y/o imagen actualizada con éxito'
          : 'User created and/or image successfully updated';

      console.log(successMessageText);
      setSuccessMessage(successMessageText);
      resetForm();
    } catch (error) {
      const errorMessageText =
        language === 'ES'
          ? 'Error al crear o actualizar el usuario'
          : 'Error creating or updating the user';
      console.error(errorMessageText + ':', error);
      setSuccessMessage(errorMessageText);
    }
  };

  return (
    <Formik
      initialValues={{
        user_idnumber: '',
        user_name: '',
        user_lastname: '',
        user_email: '',
        user_phone: '',
        main_image: [],
        id_state: 1,
        fecreg: '',
        password: '',
        enabled: 0,
        account_expired: 0,
        credentials_expired: 0,
        account_locked: 0,
        subtypeRelationship: [
          {
            type_id: 1,
            profile_details: '',
            contact_email: '',
            contact_phone: '',
            state_id: 1,
            fb_url: '',
            ig_url: '',
            ld_url: '',
            tk_url: '',
          },
        ],
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, values, errors, touched }) => {
        console.log(
          language === 'ES' ? 'Valores del formulario:' : 'Form values:',
          values,
        );
        console.log(
          language === 'ES' ? 'Errores de validación:' : 'Validation errors:',
          errors,
        );
        console.log(
          language === 'ES' ? 'Campos tocados:' : 'Touched fields:',
          touched,
        );

        const handleSendMessageToGpt = async () => {
          const message = '';
          if (!message.trim()) return;
          console.log(language === 'ES' ? 'Hola' : 'Hello');

          try {
            console.log(
              language === 'ES' ? 'Enviando mensaje:' : 'Sending message:',
              message,
            );
            const data = await chatWithGPT(message);
            const reply = data.choices[0].message.content;

            console.log(
              language === 'ES' ? 'Respuesta de ChatGPT:' : 'ChatGPT Response:',
              reply,
            );

            setChatLog(prev => [...prev, { user: message, assistant: reply }]);
          } catch (err) {
            console.error(
              language === 'ES'
                ? 'Error al llamar a ChatGPT:'
                : 'Error calling ChatGPT:',
              err,
            );
          }
          setFieldValue(
            'profile_details',
            language === 'ES'
              ? `${values.user_name} es un profesional comprometido con la excelencia. Su enfoque está centrado en ofrecer soluciones innovadoras y eficientes en su área de especialización.`
              : `${values.user_name} is a professional committed to excellence. Their focus is on delivering innovative and efficient solutions in their field of expertise.`,
          );
        };
        return (
          <Form>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              {language === 'ES' ? 'Crear Usuario' : 'Create User'}
            </Typography>

            <Box sx={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Field name="user_idnumber">
                    {({ field }) => (
                      <TextField
                        label={
                          language === 'ES'
                            ? 'Número de Identificación'
                            : 'User ID Number'
                        }
                        fullWidth
                        {...field}
                        error={Boolean(
                          touched.user_idnumber && errors.user_idnumber,
                        )}
                        helperText={
                          touched.user_idnumber && errors.user_idnumber
                        }
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="user_name">
                    {({ field }) => (
                      <TextField
                        label={language === 'ES' ? 'Nombre' : 'First Name'}
                        fullWidth
                        {...field}
                        error={Boolean(touched.user_name && errors.user_name)}
                        helperText={touched.user_name && errors.user_name}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="user_lastname">
                    {({ field }) => (
                      <TextField
                        label={language === 'ES' ? 'Apellido' : 'Last Name'}
                        fullWidth
                        {...field}
                        error={Boolean(
                          touched.user_lastname && errors.user_lastname,
                        )}
                        helperText={
                          touched.user_lastname && errors.user_lastname
                        }
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="user_email">
                    {({ field }) => (
                      <TextField
                        label={
                          language === 'ES' ? 'Correo Electrónico' : 'Email'
                        }
                        type="email"
                        fullWidth
                        {...field}
                        error={Boolean(touched.user_email && errors.user_email)}
                        helperText={touched.user_email && errors.user_email}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="user_phone">
                    {({ field }) => (
                      <TextField
                        label={language === 'ES' ? 'Teléfono' : 'Phone'}
                        fullWidth
                        {...field}
                        error={Boolean(touched.user_phone && errors.user_phone)}
                        helperText={touched.user_phone && errors.user_phone}
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Field name="main_image">
                    {({ field, form: { setFieldValue, touched, errors } }) => (
                      <>
                        <Button
                          variant="contained"
                          component="label"
                          color="primary"
                          sx={{ marginTop: '10px' }}
                        >
                          {language === 'ES'
                            ? 'Cargar Imágenes'
                            : 'Upload Images'}
                          <input
                            type="file"
                            hidden
                            multiple
                            onChange={e => {
                              const files = Array.from(e.currentTarget.files);
                              setFieldValue('main_image', files);
                              setFieldValue('imageCount', files.length);
                            }}
                            accept="image/*"
                          />
                        </Button>

                        {field.value && field.value.length > 0 && (
                          <p>
                            {language === 'ES'
                              ? `${field.value.length} imagen(es) seleccionada(s)`
                              : `${field.value.length} image(s) selected`}
                          </p>
                        )}
                        {errors.main_image && touched.main_image && (
                          <div style={{ color: 'red' }}>
                            {errors.main_image}
                          </div>
                        )}
                      </>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Field name="fecreg">
                    {({ field }) => (
                      <TextField
                        label={
                          language === 'ES'
                            ? 'Fecha de Registro'
                            : 'Date Registration'
                        }
                        type="date"
                        fullWidth
                        {...field}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={Boolean(touched.fecreg && errors.fecreg)}
                        helperText={touched.fecreg && errors.fecreg}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="password">
                    {({ field }) => (
                      <TextField
                        label={language === 'ES' ? 'Contraseña' : 'Password'}
                        fullWidth
                        {...field}
                        error={Boolean(touched.password && errors.password)}
                        helperText={touched.password && errors.password}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="enabled">
                    {({ field }) => (
                      <TextField
                        select
                        label={language === 'ES' ? 'Habilitado' : 'Enabled'}
                        fullWidth
                        {...field}
                        SelectProps={{ native: true }}
                        error={Boolean(touched.enabled && errors.enabled)}
                        helperText={touched.enabled && errors.enabled}
                      >
                        <option value="0">
                          {language === 'ES' ? 'No Habilitado' : 'Disabled'}
                        </option>
                        <option value="1">
                          {language === 'ES' ? 'Habilitado' : 'Enabled'}
                        </option>
                      </TextField>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="account_expired">
                    {({ field }) => (
                      <TextField
                        select
                        label={
                          language === 'ES'
                            ? 'Cuenta Expirada'
                            : 'Account Expired'
                        }
                        fullWidth
                        {...field}
                        SelectProps={{ native: true }}
                        error={Boolean(
                          touched.account_expired && errors.account_expired,
                        )}
                        helperText={
                          touched.account_expired && errors.account_expired
                        }
                      >
                        <option value="0">
                          {language === 'ES' ? 'No Habilitado' : 'Disabled'}
                        </option>
                        <option value="1">
                          {language === 'ES' ? 'Habilitado' : 'Enabled'}
                        </option>
                      </TextField>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="credentials_expired">
                    {({ field }) => (
                      <TextField
                        select
                        label={
                          language === 'ES'
                            ? 'Credenciales Expiradas'
                            : 'Credentials Expired'
                        }
                        fullWidth
                        {...field}
                        SelectProps={{ native: true }}
                        error={Boolean(
                          touched.credentials_expired &&
                            errors.credentials_expired,
                        )}
                        helperText={
                          touched.credentials_expired &&
                          errors.credentials_expired
                        }
                      >
                        <option value="0">
                          {language === 'ES' ? 'No Habilitado' : 'Disabled'}
                        </option>
                        <option value="1">
                          {language === 'ES' ? 'Habilitado' : 'Enabled'}
                        </option>
                      </TextField>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="account_locked">
                    {({ field }) => (
                      <TextField
                        select
                        label={
                          language === 'ES'
                            ? 'Cuenta Bloqueada'
                            : 'Account Locked'
                        }
                        fullWidth
                        {...field}
                        SelectProps={{ native: true }}
                        error={Boolean(
                          touched.account_locked && errors.account_locked,
                        )}
                        helperText={
                          touched.account_locked && errors.account_locked
                        }
                      >
                        <option value="0">
                          {language === 'ES' ? 'No Habilitado' : 'Disabled'}
                        </option>
                        <option value="1">
                          {language === 'ES' ? 'Habilitado' : 'Enabled'}
                        </option>
                      </TextField>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} sx={{ mb: 2, mt: 3 }}>
                  <Typography variant="h6">
                    {language === 'ES'
                      ? 'Relación Subtipo del Usuario'
                      : 'User Subtype Relationship'}
                  </Typography>
                  <Divider />
                </Grid>

                {values.subtypeRelationship.map((subtype, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                        {index === 0
                          ? 'Primera Relación'
                          : index === 1
                          ? 'Segunda Relación'
                          : getOrdinal().getOrdinal(index + 1) + ' Relación'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name={`subtypeRelationship[${index}].type_id`}>
                        {({ field }) => (
                          <TextField
                            select
                            label={
                              language === 'ES'
                                ? 'Tipo de Usuario'
                                : 'Type User'
                            }
                            fullWidth
                            {...field}
                            SelectProps={{ native: true }}
                            error={Boolean(
                              touched.subtypeRelationship?.[index]?.type_id &&
                                errors.subtypeRelationship?.[index]?.type_id,
                            )}
                            helperText={
                              touched.subtypeRelationship?.[index]?.type_id &&
                              errors.subtypeRelationship?.[index]?.type_id
                            }
                          >
                            <option value="1">
                              {language === 'ES' ? 'Invitado' : 'GUEST'}
                            </option>
                            <option value="2">
                              {language === 'ES' ? 'Sistema' : 'SYSTEM'}
                            </option>
                            <option value="3">
                              {language === 'ES' ? 'Anfitrión' : 'HOST'}
                            </option>
                          </TextField>
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12}>
                      <Field
                        name={`subtypeRelationship[${index}].profile_details`}
                      >
                        {() => (
                          <div>
                            <Label className={'margb10'}>
                              {language === 'ES'
                                ? 'Detalles de Perfil'
                                : 'Profile Details'}
                              <Button
                                variant="contained"
                                component="label"
                                color="secondary"
                                sx={{ marginLeft: '10px' }}
                                onClick={handleSendMessageToGpt}
                              >
                                {language === 'ES'
                                  ? 'Mejorar con IA'
                                  : 'Improve with AI'}
                              </Button>
                            </Label>
                            <Editor
                              apiKey="2qddd2ef9jx93qzld2ixgyp8lj3esyhzkninni15l4495lnb"
                              onInit={(evt, editor) =>
                                (editorRef.current[index] = editor)
                              }
                              value={
                                values.subtypeRelationship[index]
                                  .profile_details
                              }
                              init={{
                                height: 300,
                                directionality: 'ltr',
                                menubar: true,
                                language: 'en',
                                plugins: [
                                  'advlist autolink lists link image charmap print preview anchor',
                                  'searchreplace visualblocks code fullscreen',
                                  'insertdatetime media table paste code help wordcount',
                                ],
                                toolbar:
                                  'undo redo | formatselect | bold italic backcolor | \
                              alignleft aligncenter alignright alignjustify | \
                              bullist numlist outdent indent | removeformat | help',
                              }}
                              onEditorChange={content =>
                                setFieldValue(
                                  `subtypeRelationship[${index}].profile_details`,
                                  content,
                                )
                              }
                            />
                          </div>
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12}>
                      <Field
                        name={`subtypeRelationship[${index}].contact_email`}
                      >
                        {({ field }) => (
                          <TextField
                            label={
                              language === 'ES'
                                ? 'Correo de Contacto'
                                : 'Contact Email'
                            }
                            type="email"
                            fullWidth
                            {...field}
                            error={Boolean(
                              touched.subtypeRelationship?.[index]
                                ?.contact_email &&
                                errors.subtypeRelationship?.[index]
                                  ?.contact_email,
                            )}
                            helperText={
                              touched.subtypeRelationship?.[index]
                                ?.contact_email &&
                              errors.subtypeRelationship?.[index]?.contact_email
                            }
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        name={`subtypeRelationship[${index}].contact_phone`}
                      >
                        {({ field }) => (
                          <TextField
                            label={
                              language === 'ES'
                                ? 'Teléfono de Contacto'
                                : 'Contact Phone'
                            }
                            type="number"
                            fullWidth
                            {...field}
                            error={Boolean(
                              touched.subtypeRelationship?.[index]
                                ?.contact_phone &&
                                errors.subtypeRelationship?.[index]
                                  ?.contact_phone,
                            )}
                            helperText={
                              touched.subtypeRelationship?.[index]
                                ?.contact_phone &&
                              errors.subtypeRelationship?.[index]?.contact_phone
                            }
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name={`subtypeRelationship[${index}].state_id`}>
                        {({ field }) => (
                          <TextField
                            select
                            label={language === 'ES' ? 'Estado' : 'State'}
                            fullWidth
                            {...field}
                            SelectProps={{ native: true }}
                            error={Boolean(
                              touched.subtypeRelationship?.[index]?.state_id &&
                                errors.subtypeRelationship?.[index]?.state_id,
                            )}
                            helperText={
                              touched.subtypeRelationship?.[index]?.state_id &&
                              errors.subtypeRelationship?.[index]?.state_id
                            }
                          >
                            <option value="1">
                              {language === 'ES' ? 'Activo' : 'Active'}
                            </option>
                            <option value="2">
                              {language === 'ES' ? 'Inactivo' : 'Inactive'}
                            </option>
                            <option value="3">
                              {language === 'ES' ? 'Eliminado' : 'Deleted'}
                            </option>
                          </TextField>
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name={`subtypeRelationship[${index}].fb_url`}>
                        {({ field }) => (
                          <TextField
                            label={
                              language === 'ES'
                                ? 'URL de Facebook'
                                : 'Facebook URL'
                            }
                            type="url"
                            fullWidth
                            {...field}
                            error={Boolean(
                              touched.subtypeRelationship?.[index]?.fb_url &&
                                errors.subtypeRelationship?.[index]?.fb_url,
                            )}
                            helperText={
                              touched.subtypeRelationship?.[index]?.fb_url &&
                              errors.subtypeRelationship?.[index]?.fb_url
                            }
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name={`subtypeRelationship[${index}].ig_url`}>
                        {({ field }) => (
                          <TextField
                            label={
                              language === 'ES'
                                ? 'URL de Instagram'
                                : 'Instagram URL'
                            }
                            type="url"
                            fullWidth
                            {...field}
                            error={Boolean(
                              touched.subtypeRelationship?.[index]?.ig_url &&
                                errors.subtypeRelationship?.[index]?.ig_url,
                            )}
                            helperText={
                              touched.subtypeRelationship?.[index]?.ig_url &&
                              errors.subtypeRelationship?.[index]?.ig_url
                            }
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name={`subtypeRelationship[${index}].ld_url`}>
                        {({ field }) => (
                          <TextField
                            label={
                              language === 'ES'
                                ? 'URL de LinkedIn'
                                : 'LinkedIn URL'
                            }
                            type="url"
                            fullWidth
                            {...field}
                            error={Boolean(
                              touched.subtypeRelationship?.[index]?.ld_url &&
                                errors.subtypeRelationship?.[index]?.ld_url,
                            )}
                            helperText={
                              touched.subtypeRelationship?.[index]?.ld_url &&
                              errors.subtypeRelationship?.[index]?.ld_url
                            }
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name={`subtypeRelationship[${index}].tk_url`}>
                        {({ field }) => (
                          <TextField
                            label={
                              language === 'ES' ? 'URL de TikTok' : 'TikTok URL'
                            }
                            type="url"
                            fullWidth
                            {...field}
                            error={Boolean(
                              touched.subtypeRelationship?.[index]?.tk_url &&
                                errors.subtypeRelationship?.[index]?.tk_url,
                            )}
                            helperText={
                              touched.subtypeRelationship?.[index]?.tk_url &&
                              errors.subtypeRelationship?.[index]?.tk_url
                            }
                          />
                        )}
                      </Field>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid item xs={12}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    <Button
                      type="button"
                      variant="contained"
                      sx={{ paddingX: '20px', paddingY: '10px' }}
                      onClick={() => {
                        const newRelationship = {
                          type_id: 1,
                          profile_details: '',
                          contact_email: '',
                          contact_phone: '',
                          state_id: 1,
                          fb_url: '',
                          ig_url: '',
                          ld_url: '',
                          tk_url: '',
                        };
                        setFieldValue('subtypeRelationship', [
                          ...values.subtypeRelationship,
                          newRelationship,
                        ]);
                      }}
                      disabled={values.subtypeRelationship.length >= 3} // máximo 3
                    >
                      {language === 'ES'
                        ? 'Agregar nueva relación'
                        : 'Add new relationship'}
                    </Button>

                    {values.subtypeRelationship.length > 1 && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          const newRelationships =
                            values.subtypeRelationship.slice(0, -1);
                          setFieldValue(
                            'subtypeRelationship',
                            newRelationships,
                          );
                        }}
                        sx={{
                          paddingX: '20px',
                          paddingY: '10px',
                          backgroundColor: 'red',
                          color: 'white',
                          borderColor: 'red',
                          '&:hover': {
                            backgroundColor: '#d32f2f',
                            borderColor: '#d32f2f',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                          },
                          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                          marginLeft: '10px',
                        }}
                      >
                        {language === 'ES'
                          ? 'Eliminar relación'
                          : 'Remove relationship'}
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
              {successMessage && (
                <Box sx={{ marginTop: '20px', textAlign: 'center' }}>
                  <Alert
                    severity={
                      successMessage.includes('Error') ? 'error' : 'success'
                    }
                  >
                    {successMessage}
                  </Alert>
                </Box>
              )}
              <Box sx={{ textAlign: 'center', marginTop: '20px' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ paddingX: '20px', paddingY: '10px' }}
                >
                  {language === 'ES' ? 'Crear Usuario' : 'Create User'}
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default UseForm;
